import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntegrationGrid = () => {
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const integrations = [
    // Featured OpenAI Integration
    {
      name: 'OpenAI (GPT-4)',
      category: 'AI',
      icon: 'Zap',
      adoption: '98%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '5 minutes',
      description: 'Power your AI Buddy with GPT-4 for context-aware employee assistance, policy lookup, and workflow automation.',
      features: ['Real-time AI responses', 'Context-aware answers', 'Policy document search', 'Automated ticket creation'],
      isFeatured: true
    },
    // Communication
    {
      name: 'Slack',
      category: 'Communication',
      icon: 'MessageSquare',
      adoption: '96%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '2 minutes',
      description: 'Sync messages, automate notifications, and enable seamless team collaboration.',
      features: ['Message sync', 'Channel automation', 'User status integration', 'File sharing']
    },
    {
      name: 'Microsoft Teams',
      category: 'Communication',
      icon: 'Users',
      adoption: '96%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '3 minutes',
      description: 'Full Microsoft 365 integration with chat, meetings, and collaboration tools.',
      features: ['Chat integration', 'Meeting schedules', 'File collaboration', 'Presence status']
    },
    {
      name: 'Zoom',
      category: 'Communication',
      icon: 'Video',
      adoption: '94%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '2 minutes',
      description: 'Schedule meetings, sync recordings, and automate video collaboration.',
      features: ['Meeting scheduling', 'Recording access', 'Participant data', 'Calendar sync']
    },
    // Knowledge
    {
      name: 'Google Drive',
      category: 'Knowledge',
      icon: 'FolderOpen',
      adoption: '92%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '5 minutes',
      description: 'Search, access, and sync documents across your organization.',
      features: ['Document search', 'File sync', 'Permission management', 'Version control']
    },
    {
      name: 'SharePoint',
      category: 'Knowledge',
      icon: 'FileText',
      adoption: '92%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '7 minutes',
      description: 'Enterprise document management with advanced search and collaboration.',
      features: ['Document library', 'Metadata search', 'Workflow automation', 'Compliance tools']
    },
    {
      name: 'Notion',
      category: 'Knowledge',
      icon: 'BookOpen',
      adoption: '88%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '3 minutes',
      description: 'Sync wikis, databases, and collaborative workspaces.',
      features: ['Wiki integration', 'Database sync', 'Page templates', 'Real-time collaboration']
    },
    {
      name: 'Confluence',
      category: 'Knowledge',
      icon: 'Layers',
      adoption: '85%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '6 minutes',
      description: 'Atlassian knowledge base integration with advanced search.',
      features: ['Page search', 'Space management', 'Macro support', 'Content templates']
    },
    // CRM
    {
      name: 'Salesforce',
      category: 'CRM',
      icon: 'BarChart',
      adoption: '89%',
      complexity: 'Advanced',
      status: 'Available',
      setupTime: '15 minutes',
      description: 'Full CRM integration with customer data, leads, and sales pipelines.',
      features: ['Lead management', 'Customer data', 'Pipeline tracking', 'Custom objects']
    },
    {
      name: 'HubSpot',
      category: 'CRM',
      icon: 'TrendingUp',
      adoption: '87%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '8 minutes',
      description: 'Marketing, sales, and service hub integration.',
      features: ['Contact sync', 'Deal tracking', 'Email campaigns', 'Analytics']
    },
    // HRIS
    {
      name: 'Workday',
      category: 'HRIS',
      icon: 'Briefcase',
      adoption: '82%',
      complexity: 'Advanced',
      status: 'Enterprise',
      setupTime: '20 minutes',
      description: 'Enterprise HR, payroll, and talent management integration.',
      features: ['Employee data', 'Payroll sync', 'Benefits management', 'Time tracking']
    },
    {
      name: 'BambooHR',
      category: 'HRIS',
      icon: 'UserCheck',
      adoption: '80%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '10 minutes',
      description: 'HR management system with employee records and time-off tracking.',
      features: ['Employee profiles', 'Time-off requests', 'Performance reviews', 'Reporting']
    },
    // Engineering
    {
      name: 'GitHub',
      category: 'Engineering',
      icon: 'Github',
      adoption: '92%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '4 minutes',
      description: 'Code repository integration with PR tracking and issue management.',
      features: ['Repository access', 'PR notifications', 'Issue tracking', 'Commit history']
    },
    {
      name: 'Jira',
      category: 'Engineering',
      icon: 'CheckSquare',
      adoption: '85%',
      complexity: 'Moderate',
      status: 'Available',
      setupTime: '7 minutes',
      description: 'Project management and issue tracking for agile teams.',
      features: ['Sprint tracking', 'Issue management', 'Custom workflows', 'Reporting']
    },
    {
      name: 'Linear',
      category: 'Engineering',
      icon: 'Zap',
      adoption: '75%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '3 minutes',
      description: 'Modern issue tracking and project management for product teams.',
      features: ['Issue tracking', 'Project views', 'Keyboard shortcuts', 'API access']
    },
    // SSO
    {
      name: 'Okta',
      category: 'SSO',
      icon: 'Lock',
      adoption: '95%',
      complexity: 'Advanced',
      status: 'Enterprise',
      setupTime: '25 minutes',
      description: 'Enterprise identity and access management with SSO and MFA.',
      features: ['Single Sign-On', 'Multi-factor auth', 'User provisioning', 'Audit logs']
    },
    {
      name: 'Azure AD',
      category: 'SSO',
      icon: 'Shield',
      adoption: '95%',
      complexity: 'Advanced',
      status: 'Enterprise',
      setupTime: '20 minutes',
      description: 'Microsoft identity platform with SSO and conditional access.',
      features: ['Azure SSO', 'Conditional access', 'Identity protection', 'B2B collaboration']
    },
    // Additional Tools
    {
      name: 'Airtable',
      category: 'Tools',
      icon: 'Database',
      adoption: '70%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '5 minutes',
      description: 'Flexible database and spreadsheet platform for custom workflows.',
      features: ['Custom databases', 'Views & filters', 'Automations', 'API access']
    },
    {
      name: 'Figma',
      category: 'Tools',
      icon: 'Figma',
      adoption: '78%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '3 minutes',
      description: 'Design collaboration platform integration.',
      features: ['File access', 'Comment sync', 'Version history', 'Team libraries']
    },
    {
      name: 'Miro',
      category: 'Tools',
      icon: 'Layout',
      adoption: '72%',
      complexity: 'Simple',
      status: 'Available',
      setupTime: '4 minutes',
      description: 'Visual collaboration whiteboard for distributed teams.',
      features: ['Board access', 'Real-time collaboration', 'Templates', 'Export options']
    }
  ];

  return (
    <section className="py-16 bg-[#0F0F11]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Featured Integration (OpenAI) */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Icon name="Star" size={20} color="#FFD100" />
            <h3 className="text-2xl font-bold text-white">Featured Integration</h3>
          </div>
          {integrations?.filter((int) => int?.isFeatured)?.map((integration, index) => (
              <div
                key={index}
                className="backdrop-blur-xl bg-gradient-to-r from-[#FFD100]/10 to-[#FFD100]/5 border-2 border-[#FFD100] rounded-3xl p-8 shadow-2xl shadow-[#FFD100]/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 bg-[#FFD100] rounded-2xl flex items-center justify-center">
                        <Icon name={integration?.icon} size={32} color="#0B1220" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-2xl font-bold text-white mb-2">{integration?.name}</h4>
                        <p className="text-white/70 mb-4">{integration?.description}</p>
                        <div className="flex flex-wrap gap-3">
                          <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-emerald-400 text-xs font-semibold">
                            {integration?.status}
                          </span>
                          <span className="px-3 py-1 bg-white/5 border border-white/20 rounded-lg text-white/70 text-xs">
                            {integration?.complexity} Setup
                          </span>
                          <span className="px-3 py-1 bg-white/5 border border-white/20 rounded-lg text-white/70 text-xs">
                            {integration?.setupTime}
                          </span>
                          <span className="px-3 py-1 bg-white/5 border border-white/20 rounded-lg text-white/70 text-xs">
                            {integration?.adoption} Enterprise Adoption
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {integration?.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Icon name="Check" size={16} color="#FFD100" strokeWidth={3} />
                          <span className="text-white/80 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center gap-4">
                    <Button
                      variant="default"
                      size="lg"
                      className="bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-bold w-full">
                      Install Integration
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-2 border-white/20 text-white hover:bg-white/10 w-full">
                      View Documentation
                    </Button>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* All Integrations Grid */}
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-white mb-6">All Integrations ({integrations?.length})</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations?.filter((int) => !int?.isFeatured)?.map((integration, index) => (
              <div
                key={index}
                onClick={() => setSelectedIntegration(selectedIntegration === index ? null : index)}
                className="group backdrop-blur-sm bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-[#FFD100]/40 hover:shadow-xl hover:shadow-[#FFD100]/10 transition-all duration-300 cursor-pointer">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 bg-[#FFD100]/10 rounded-xl flex items-center justify-center group-hover:bg-[#FFD100]/20 group-hover:scale-110 transition-all duration-300">
                    <Icon name={integration?.icon} size={28} color="#FFD100" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-1">{integration?.name}</h4>
                    <p className="text-white/50 text-xs">{integration?.category}</p>
                  </div>
                </div>

                <p className="text-white/70 text-sm mb-4 line-clamp-2">{integration?.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span
                    className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                      integration?.status === 'Available' ?'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' :'bg-[#FFD100]/20 border border-[#FFD100]/40 text-[#FFD100]'
                    }`}>
                    {integration?.status}
                  </span>
                  <span className="px-2 py-1 bg-white/5 border border-white/20 rounded-lg text-white/60 text-xs">
                    {integration?.complexity}
                  </span>
                </div>

                {selectedIntegration === index && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <div className="text-xs space-y-2">
                      {integration?.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Icon name="Check" size={12} color="#FFD100" strokeWidth={3} />
                          <span className="text-white/70">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="default" size="sm" className="flex-1 bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-semibold text-xs">
                        Install
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1 border border-white/20 text-white hover:bg-white/10 text-xs">
                        Details
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Custom Integration CTA */}
        <div className="mt-16 backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-12 text-center">
          <Icon name="Code" size={48} color="#FFD100" className="mx-auto mb-6" />
          <h3 className="text-3xl font-bold text-white mb-4">Need a Custom Integration?</h3>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Our API and webhook system lets you build custom integrations with any tool. Enterprise plans include dedicated integration support.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="default" size="lg" className="bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-bold">
              View API Documentation
            </Button>
            <Button variant="outline" size="lg" className="border-2 border-white/20 text-white hover:bg-white/10">
              Contact Integration Team
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationGrid;